import { useQuery } from "@tanstack/react-query"
import { CircleAlert, FileDown, Lock } from "lucide-react"
import { useRef, useState } from "react"
import { useParams } from "react-router"

import { api } from "@/api/cliente"
import { LogoSetieca } from "@/componentes/marca/LogoSetieca"
import { descargar, logoComoPng, pdfDelReporte } from "@/componentes/reportes/pdfReporte"
import { CLASE, plural, porOrigen, type Reporte, type Saldo } from "@/componentes/reportes/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

/**
 * Las cuentas por cobrar o por pagar del 2.0 vistas desde el enlace del parte de las
 * 7:05 (`/r/<token>`). **Sin sesión, sin menú y sin poder tocar nada**, como
 * `OrdenPublica`: lo que se ve lo decide una lista blanca del servidor
 * (`ReportePublico`) y no hay un solo enlace hacia otra pantalla.
 *
 * ⚠ **Es la FOTO del momento del aviso**, no una consulta en vivo (decidido el 12/10):
 * dice las cifras del mensaje que lo trajo. La antigüedad está contada contra el día del
 * reporte.
 *
 * ⚠ **En el teléfono cada cuenta es una TARJETA** (`.tabla-apilable`, menos de 640 px):
 * el expediente de título y debajo «CONCEPTO ··· valor». Con ancho mínimo y
 * desplazamiento lateral, ocho columnas en 360 px no se leían. De 640 en adelante es la
 * tabla, que tiene que CABER: cifras y fechas sin partir, y la emisión fuera hasta
 * 1024 px —la antigüedad en días dice lo mismo—.
 *
 * ⚠ **«Exportar PDF» COMPONE el archivo y lo descarga** (`componentes/reportes/
 * pdfReporte.ts`), no llama a `window.print()`: la impresión no hace nada en buena
 * parte de los móviles —el navegador de Telegram, entre ellos—. Ctrl+P sigue sirviendo
 * en el escritorio (`.hoja-reporte` en `globales.css`).
 *
 * ⚠ **Membrete con la marca de la empresa** (12/10), el vectorial `LogoSetieca`: el logo
 * subido en «Empresa» se sirve con sesión y esta página no la tiene. El nombre y el RIF
 * vienen en la respuesta.
 */

function saldos(lista: Saldo[]): string {
  return lista.length === 0 ? "—" : lista.map((s) => `${s.moneda} ${monto(s.saldo)}`).join(" + ")
}

export function ReportePublico() {
  const { token = "" } = useParams()
  const marca = useRef<HTMLDivElement>(null)
  const [generando, setGenerando] = useState(false)
  const [fallo, setFallo] = useState(false)
  const consulta = useQuery({
    queryKey: ["publico", "reporte", token],
    queryFn: () => api.get<Reporte>(`/api/v1/publico/reportes/${token}`),
    retry: false,
  })

  if (consulta.isPending) {
    return (
      <p role="status" className="p-10 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }
  if (consulta.isError || !consulta.data) {
    return (
      <div className="mx-auto max-w-lg p-10 text-center">
        <CircleAlert size={28} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        <h1 className="mt-3 text-lg font-semibold">Este enlace ya no vale</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Puede haber caducado. El de mañana trae el reporte nuevo.
        </p>
      </div>
    )
  }

  const r = consulta.data
  const tenue = { color: "var(--fg-muted)" }
  const borde = { borderColor: "var(--border)" }

  async function exportar() {
    setGenerando(true)
    setFallo(false)
    try {
      const logo = await logoComoPng(
        marca.current?.querySelector("svg") ?? null,
        "rgb(122, 27, 56)",
      )
      descargar(
        await pdfDelReporte(r, logo),
        `${r.titulo} ${comoFecha(r.fecha).replaceAll("/", "-")}.pdf`,
      )
    } catch {
      setFallo(true)
    } finally {
      setGenerando(false)
    }
  }

  return (
    <div className="hoja-reporte mx-auto max-w-6xl p-4 sm:p-8">
      <div className="no-print mb-4 flex flex-wrap items-center justify-between gap-3">
        <p
          className="flex items-center gap-2 rounded-[var(--radius-control)] border border-dashed px-3 py-2 text-xs"
          style={{ ...borde, ...tenue }}
        >
          <Lock size={12} aria-hidden={true} />
          Vista de sólo lectura. El enlace caduca el {comoFecha(r.expira_en)}.
        </p>
        <button
          type="button"
          onClick={exportar}
          disabled={generando}
          className="foco-cristal inline-flex min-h-11 items-center gap-2 rounded-[var(--radius-control)] border px-4 text-sm font-medium disabled:opacity-60"
          style={borde}
        >
          <FileDown size={16} aria-hidden={true} />
          {generando ? "Generando…" : "Exportar PDF"}
        </button>
        {fallo && (
          <p role="alert" className="w-full text-xs" style={{ color: "var(--destructive)" }}>
            No se pudo generar el PDF. Vuelve a intentarlo.
          </p>
        )}
      </div>

      <header className="mb-5 flex items-center justify-between gap-4 border-b pb-3" style={borde}>
        <div ref={marca} className="shrink-0" style={{ color: "var(--fg)" }}>
          <LogoSetieca titulo={r.empresa_nombre} className="h-12 w-auto sm:h-14" />
        </div>
        <div className="min-w-0 text-right">
          <p className="text-sm font-semibold uppercase sm:text-base">{r.empresa_nombre}</p>
          {r.empresa_rif ? (
            <p className="text-xs" style={tenue}>
              RIF {r.empresa_rif}
            </p>
          ) : null}
        </div>
      </header>

      <p className="text-xs" style={tenue}>
        Reporte del {comoFecha(r.fecha)}, generado el {comoFechaHora(r.generado_en)}
      </p>
      <h1 className="mt-1 text-xl font-semibold">{r.titulo} abiertas</h1>

      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Cuentas abiertas</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">
            {r.total_cuentas} · {r.grupos.length} {plural(r.rotulo_tercero)}
          </dd>
        </div>
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Saldo</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">{saldos(r.saldos)}</dd>
        </div>
        <div className="rounded-[var(--radius-control)] border p-3" style={borde}>
          <dt style={tenue}>Vencidas</dt>
          <dd className="mt-1 text-base font-semibold tabular-nums">
            {r.vencidas}
            {r.vencidas > 0 ? ` · ${saldos(r.saldos_vencidos)}` : ""}
          </dd>
        </div>
      </dl>

      <h2 className="mt-6 text-sm font-semibold">Por antigüedad</h2>
      <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-3 lg:grid-cols-5">
        {r.tramos.map((t) => (
          <li key={t.rotulo} className="tabular-nums">
            <span style={tenue}>{t.rotulo}:</span> {saldos(t.saldos)}
          </li>
        ))}
      </ul>

      {porOrigen(r).length > 0 && (
        <>
          <h2 className="mt-6 text-sm font-semibold">Por origen</h2>
          <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
            {porOrigen(r).map((o) => (
              <li key={o.clase} className="tabular-nums">
                <span style={tenue}>{o.rotulo}:</span> {saldos(o.saldos)}
              </li>
            ))}
          </ul>
        </>
      )}

      {r.grupos.length === 0 ? (
        <p className="mt-8 text-sm" style={tenue}>
          No había cuentas abiertas.
        </p>
      ) : (
        r.grupos.map((g) => (
          <section key={g.tercero} className="grupo-reporte mt-8">
            <div
              className="flex flex-wrap items-baseline justify-between gap-2 border-b pb-1"
              style={borde}
            >
              <h2 className="text-base font-semibold">
                {g.tercero}
                {g.rif ? (
                  <span className="ml-2 text-xs font-normal" style={tenue}>
                    {g.rif}
                  </span>
                ) : null}
              </h2>
              <p className="text-sm font-semibold tabular-nums">{saldos(g.saldos)}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="tabla-apilable mt-2 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs" style={tenue}>
                    <th className="py-1 pr-3 font-medium">Expediente</th>
                    <th className="py-1 pr-3 font-medium">Concepto</th>
                    <th className="py-1 pr-3 font-medium sm:max-lg:hidden">Emisión</th>
                    <th className="py-1 pr-3 font-medium">Vence</th>
                    <th className="py-1 pr-3 text-right font-medium">Días</th>
                    <th className="py-1 pr-3 text-right font-medium">Monto</th>
                    <th className="py-1 pr-3 text-right font-medium">Abonado</th>
                    <th className="py-1 text-right font-medium">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {g.cuentas.map((c) => (
                    <tr key={c.id} className="border-t align-top" style={borde}>
                      <td data-rotulo="Expediente" className="py-1 pr-3 tabular-nums">
                        {c.expediente ? (
                          <>
                            <span className="sm:hidden">Expediente </span>
                            {c.expediente}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td data-rotulo="Concepto" className="py-1 pr-3">
                        {c.concepto}
                        <span className="block text-xs" style={tenue}>
                          {CLASE[c.clase] ?? c.clase}
                        </span>
                      </td>
                      <td
                        data-rotulo="Emisión"
                        className="whitespace-nowrap py-1 pr-3 tabular-nums sm:max-lg:hidden"
                      >
                        {comoFecha(c.emision) || "—"}
                      </td>
                      <td data-rotulo="Vence" className="whitespace-nowrap py-1 pr-3 tabular-nums">
                        {comoFecha(c.vence) || "—"}
                        {c.vencida ? (
                          <span
                            className="block text-xs font-semibold"
                            style={{ color: "var(--destructive)" }}
                          >
                            Vencida
                          </span>
                        ) : null}
                      </td>
                      <td data-rotulo="Días" className="py-1 pr-3 text-right tabular-nums">
                        {c.antiguedad}
                      </td>
                      <td
                        data-rotulo="Monto"
                        className="whitespace-nowrap py-1 pr-3 text-right tabular-nums"
                      >
                        {c.moneda} {monto(c.monto)}
                      </td>
                      <td
                        data-rotulo="Abonado"
                        className="whitespace-nowrap py-1 pr-3 text-right tabular-nums"
                      >
                        {monto(c.abonado)}
                      </td>
                      <td
                        data-rotulo="Saldo"
                        className="whitespace-nowrap py-1 text-right font-medium tabular-nums"
                      >
                        {c.moneda} {monto(c.saldo)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}
    </div>
  )
}
